/**
 * On-device view of canvas input accounting.
 *
 * An iPad has no devtools without a tethered Mac, and a lost stroke leaves no
 * ink and throws nothing — so without this a bug in the room can only be
 * described, not measured. The numbers themselves come from
 * `canvasInputTelemetry`, which runs whether or not this is mounted; this is
 * only a window onto them.
 *
 * Opt in with `?canvasdebug=1`. Two conditions, both required, because a
 * diagnostic overlay appearing over a student's work is a defect in itself:
 *
 *  - the flag is set for THIS TAB, and
 *  - the `canvas_input_tracing` gate is on for this user.
 *
 * The gate is off globally in prod, so the URL alone cannot raise the box on a
 * real student's device; someone has to have deliberately turned that user up.
 * On dev the gate is open, so the flag alone is enough.
 */

import { useEffect, useState } from 'react';
import { isFeatureEnabled } from '@shared-ui/feature-gates';
import { clearPalmWrites, getPalmRejectionSetting, getPalmWrites, isPencilSeen } from '../palmRejection';
import {
    resetCanvasInputStats,
    subscribeCanvasInput,
    type InputSnapshot,
} from './canvasInputTelemetry';

const DEBUG_KEY = 'canvas.debug';

/** Injected by vite.config.ts at build time — a stale deploy should be visible
 *  on the device rather than inferred from behaviour. */
declare const __BUILD_TIME__: string;

/**
 * Remembered for the tab, not the device.
 *
 * `?canvasdebug=1` is typed against whatever URL is on screen and is lost the
 * moment the app routes into the solving page, so it has to outlive the
 * navigation — but in localStorage it outlived the investigation too, and
 * reappeared for the next person to use that iPad. sessionStorage covers the
 * routing and ends with the tab.
 */
function debugFlagSet(): boolean {
    try {
        const param = new URLSearchParams(window.location.search).get('canvasdebug');
        if (param === '1') {
            window.sessionStorage.setItem(DEBUG_KEY, '1');
            return true;
        }
        if (param === '0') {
            window.sessionStorage.removeItem(DEBUG_KEY);
            return false;
        }
        // Clear any device-level flag left by the earlier sticky behaviour.
        window.localStorage.removeItem(DEBUG_KEY);
        return window.sessionStorage.getItem(DEBUG_KEY) === '1';
    } catch {
        return false;
    }
}

export function isCanvasDebugEnabled(): boolean {
    return debugFlagSet() && isFeatureEnabled('canvas_input_tracing');
}

const round1 = (n: number) => Math.round(n * 10) / 10;

function buildStamp(): string {
    try {
        return new Date(__BUILD_TIME__).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '?';
    }
}

export function CanvasDebugOverlay() {
    const [snap, setSnap] = useState<InputSnapshot | null>(null);
    const [enabled, setEnabled] = useState(isCanvasDebugEnabled);

    useEffect(() => {
        if (!enabled) return;
        return subscribeCanvasInput(setSnap);
    }, [enabled]);

    if (!enabled || !snap) return null;

    const pens = snap.contacts.filter(c => c.pointerType === 'pen');
    const lost = pens.filter(c => c.closed && !c.path);
    const lostLog = lost.length === 0
        ? 'none'
        : lost.map(c => `#${c.seq}(${c.moves}/${c.samples})`).join(' ');
    const okLog = pens.filter(c => c.path).slice(-3)
        .map(c => `${c.moves}/${c.samples}`).join(' ') || '—';
    const touchLog = snap.contacts.filter(c => c.pointerType === 'touch').slice(-4)
        .map(c => `${c.touchType}:${c.moves}/${c.samples}[w${round1(c.width)} r${round1(c.radius)}]`)
        .join(' ') || 'none';
    const lastPen = pens[pens.length - 1];
    const writes = getPalmWrites();
    const writeLog = writes.length === 0
        ? '—'
        : writes.map(w => `${w.setting}←${w.from || '?'}`).join('  ');

    return (
        <div
            style={{
                position: 'fixed',
                top: 8,
                right: 8,
                zIndex: 2147483647,
                padding: '8px 10px',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.85)',
                color: '#fff',
                font: '600 12px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace',
                pointerEvents: 'none',
                whiteSpace: 'pre',
            }}
        >
            {`build ${buildStamp()}
pen↓ ${snap.penDown}   pen↑ ${snap.penUp}
paths ${snap.paths}   unmatched ${snap.penDown - snap.paths}
cancel ${snap.penCancel}   touch↓ ${snap.touchDown}
isPrimary ${lastPen ? String(lastPen.isPrimary) : '—'}
gap ${lastPen?.gapMs == null ? '—' : `${lastPen.gapMs}ms`}
palm ${isPencilSeen() ? 'seen' : 'NOT-seen'}/${getPalmRejectionSetting()}
lost ${lostLog}
ok ${okLog}
tch ${touchLog}
writes ${writeLog}`}
            <div style={{ pointerEvents: 'auto', marginTop: 6, display: 'flex', gap: 8 }}>
                <button
                    onClick={() => { clearPalmWrites(); resetCanvasInputStats(); }}
                    style={{ font: 'inherit' }}
                >
                    reset
                </button>
                <button
                    onClick={() => {
                        try { window.sessionStorage.removeItem(DEBUG_KEY); } catch { /* ignore */ }
                        setEnabled(false);
                    }}
                    style={{ font: 'inherit' }}
                >
                    close
                </button>
            </div>
        </div>
    );
}
